import { useCallback } from "react";
import { useNavigate, useParams } from "react-router";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageVCS } from "hooks/useAnalytics/pages/vcs";
import { VcsIntegration } from "types/generated";

import { showVCSDeleteIntegrationModal } from "../VcsShared/components/DeleteIntegrationModal";
import { isTestIntegrationConnectionAvailable } from "../VcsShared/helpers";
import useDeleteVcsIntegration from "../VcsShared/useDeleteVcsIntegration";
import useTestVCSIntegrationConnection from "../VcsShared/useTestVCSIntegrationConnection";

type VCSIntegrationDropdownActionsProps = {
  integration: VcsIntegration;
  onEdit: () => void;
  totalEntitiesCount: number;
};

const VCSIntegrationDropdownActions = ({
  integration,
  onEdit,
  totalEntitiesCount,
}: VCSIntegrationDropdownActionsProps) => {
  const navigate = useNavigate();
  const params = useParams();

  const { testIntegrationConnection, loading } = useTestVCSIntegrationConnection({
    id: integration.id,
    name: integration.name || `Default ${HUMANIZE_PROVIDER[integration.provider]}`,
    provider: integration.provider,
  });

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageVCS.VCSIntegrations,
  });

  const { deleteVcsIntegration } = useDeleteVcsIntegration();

  const isTestConnectionAvailable = isTestIntegrationConnectionAvailable(integration.details);

  const goToVcsIntegrations = useCallback(() => {
    navigate(`/integrations/${params.integrationGroup}`);
  }, [navigate, params]);

  const handleDeleteVcsIntegration = useCallback(() => {
    // FYI: the ID is not available only for the built-in GitHub integration & the delete option is not available for it
    if (integration.id) {
      deleteVcsIntegration(
        integration.id,
        integration.provider,
        totalEntitiesCount,
        goToVcsIntegrations
      );
    }
  }, [
    deleteVcsIntegration,
    goToVcsIntegrations,
    integration.id,
    integration.provider,
    totalEntitiesCount,
  ]);

  const onDeleteConfirmationModalShow = useCallback(() => {
    showVCSDeleteIntegrationModal({
      attachedEntitiesQty: totalEntitiesCount,
      integrationName: integration.name,
      onConfirm: handleDeleteVcsIntegration,
    });

    trackSegmentAnalyticsEvent("Integration - Unlink Start");
  }, [
    handleDeleteVcsIntegration,
    integration.name,
    totalEntitiesCount,
    trackSegmentAnalyticsEvent,
  ]);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize="medium">
      <CopyFieldDropdownMenuItem title="Copy ID" value={integration.id} />
      {isTestConnectionAvailable && (
        <DropdownMenuItem onAction={testIntegrationConnection} loading={loading}>
          Test
        </DropdownMenuItem>
      )}

      <DropdownMenuItem onAction={onEdit}>Edit</DropdownMenuItem>
      <DropdownMenuItem onAction={onDeleteConfirmationModalShow} danger>
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default VCSIntegrationDropdownActions;
